import { useMemo } from 'react';
import graphqlClient from '@/graphql/graphql-client';
import { useQuery } from '@tanstack/react-query';
import { useMyOgnization } from '@/api-client/use-my-ognization';
import { useGetSignatureOrgs } from '@/api-client/use-get-signature-orgs';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import { useMyOgnizationStore } from '@/store';

import { eqFromWhere } from '@/graphql/list-query';
import {
  OrganizationsByBlocksOrgIdDocument,
  OrganizationsByItemIdDocument,
  OrganizationsBySigOrgIdDocument,
  OrganizationsBySlugDocument,
  OrganizationsDocument,
  type OrganizationsQuery,
  type OrganizationsQueryVariables,
} from '@/graphql/generated/graphql';

export type OrganizationItem = NonNullable<
  NonNullable<OrganizationsQuery['getOrganizationss']>['items'][number]
>;

export type GetOrganizationsResponse = OrganizationsQuery;

type OrganizationWhere = {
  ItemId?: { eq?: string | null };
  Slug?: { eq?: string | null };
  BlocksOrgId?: { eq?: string | null };
  SigOrgId?: { eq?: string | null };
  and?: unknown[];
  or?: unknown[];
};

export type OrganizationsListVariables = {
  where?: OrganizationWhere;
  paging?: { pageNo: number; pageSize: number };
};

export const SYSTEM_ADMIN_ORG_ID =
  (import.meta.env.VITE_SYSTEM_ADMIN_ORG_ID as string | undefined) ?? '';

export const notDeletedWhere = () => ({
  or: [{ IsDeleted: { eq: false } }, { IsDeleted: { eq: null } }],
});

const isActiveOrg = (org?: OrganizationItem | null) => Boolean(org && !org.IsDeleted);

export const requestOrganizations = (input?: OrganizationsListVariables) => {
  const pageNo = input?.paging?.pageNo ?? 1;
  const pageSize = input?.paging?.pageSize ?? 10;
  const where = input?.where;
  const itemId = eqFromWhere(where, 'ItemId');
  const slug = eqFromWhere(where, 'Slug');
  const blocksOrgId = eqFromWhere(where, 'BlocksOrgId');
  const sigOrgId = eqFromWhere(where, 'SigOrgId');

  if (itemId) {
    return graphqlClient.request(OrganizationsByItemIdDocument, {
      itemId,
      pageNo,
      pageSize,
    });
  }
  if (slug) {
    return graphqlClient.request(OrganizationsBySlugDocument, {
      slug,
      pageNo,
      pageSize,
    });
  }
  if (blocksOrgId) {
    return graphqlClient.request(OrganizationsByBlocksOrgIdDocument, {
      blocksOrgId,
      pageNo,
      pageSize,
    });
  }
  if (sigOrgId) {
    return graphqlClient.request(OrganizationsBySigOrgIdDocument, {
      sigOrgId,
      pageNo,
      pageSize,
    });
  }
  return graphqlClient.request(OrganizationsDocument, {
    pageNo,
    pageSize,
  });
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const useGetOrganizations = (variables?: OrganizationsListVariables) => {
  return useQuery({
    queryKey: ['organizations', variables],
    queryFn: () => requestOrganizations(variables),
  });
};

export const useOrganizations = (variables: OrganizationsQueryVariables) => {
  return useQuery({
    queryKey: ['organizations', variables],
    queryFn: () => graphqlClient.request(OrganizationsDocument, variables),
  });
};

/**
 * Fetch organizations filtered by Blocks org IDs.
 * `requireSignatureLink: true` (default) — only orgs with a SigOrgId or the
 * system-admin org are returned. Use false for authorization lookups.
 */
export const fetchOrganizationsByBlocksOrgIds = async (
  blocksOrgIds: string[],
  requireSignatureLink = false
): Promise<OrganizationItem[]> => {
  if (blocksOrgIds.length === 0) return [];

  const responses = await Promise.all(
    blocksOrgIds.map((blocksOrgId) =>
      requestOrganizations({
        where: { BlocksOrgId: { eq: blocksOrgId } },
        paging: { pageNo: 1, pageSize: 1 },
      })
    )
  );

  return responses.flatMap((response) => {
    const item = response?.getOrganizationss?.items?.[0];
    if (!isActiveOrg(item)) return [];
    if (
      requireSignatureLink &&
      !item.SigOrgId &&
      item.BlocksOrgId !== SYSTEM_ADMIN_ORG_ID
    ) {
      return [];
    }
    return [item];
  });
};

export const fetchOrganizationsBySigOrgIds = async (
  sigOrgIds: string[]
): Promise<OrganizationItem[]> => {
  if (sigOrgIds.length === 0) return [];

  const responses = await Promise.all(
    sigOrgIds.map((sigOrgId) =>
      requestOrganizations({
        where: { SigOrgId: { eq: sigOrgId } },
        paging: { pageNo: 1, pageSize: 1 },
      })
    )
  );

  return responses.flatMap((response) => {
    const item = response?.getOrganizationss?.items?.[0];
    return isActiveOrg(item) ? [item] : [];
  });
};

export const useGetOrganizationsByBlocksOrgIds = (
  blocksOrgIds: string[],
  opts: { requireSignatureLink?: boolean; enabled?: boolean } = {}
) => {
  const { requireSignatureLink = true, enabled = true } = opts;

  return useQuery({
    queryKey: ['organizations-by-blocks-ids', blocksOrgIds, requireSignatureLink],
    queryFn: () => fetchOrganizationsByBlocksOrgIds(blocksOrgIds, requireSignatureLink),
    enabled: enabled && blocksOrgIds.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const fetchOrganizationById = async (
  orgId: string
): Promise<OrganizationItem | null> => {
  if (!orgId) return null;

  try {
    const res = await requestOrganizations({
      where: { ItemId: { eq: orgId } },
      paging: { pageNo: 1, pageSize: 1 },
    });
    const match = res?.getOrganizationss?.items?.[0];
    if (isActiveOrg(match)) return match;
  } catch {
    // Continue to next lookup strategy
  }

  // 3. Try by BlocksOrgId directly (IAM organization ID)
  try {
    const res = await requestOrganizations({
      where: { BlocksOrgId: { eq: orgId } },
      paging: { pageNo: 1, pageSize: 1 },
    });
    const match = res?.getOrganizationss?.items?.[0];
    if (isActiveOrg(match)) return match;
  } catch {
    // Continue to next lookup strategy
  }

  // 4. Try by Slug directly
  try {
    const res = await requestOrganizations({
      where: { Slug: { eq: orgId } },
      paging: { pageNo: 1, pageSize: 1 },
    });
    const match = res?.getOrganizationss?.items?.[0];
    if (isActiveOrg(match)) return match;
  } catch {
    // Continue to next lookup strategy
  }

  // 5. Fallback: check IAM organizations from the local store
  try {
    const iamOrgs = useMyOgnizationStore.getState().organization?.organizations ?? [];
    const iamMatch = iamOrgs.find((o) => o.itemId === orgId);
    if (iamMatch) {
      return {
        ItemId: iamMatch.itemId,
        BlocksOrgId: iamMatch.itemId,
        Name: iamMatch.name,
        Slug: iamMatch.itemId,
        MaxUsers: 0,
        MaxTemplates: 0,
      } satisfies OrganizationItem;
    }

    const currentOrg = useMyOgnizationStore.getState().currentOrganization;
    if (currentOrg && currentOrg.itemId === orgId) {
      return {
        ItemId: currentOrg.itemId,
        BlocksOrgId: currentOrg.itemId,
        Name: currentOrg.name,
        Slug: currentOrg.itemId,
        MaxUsers: 0,
        MaxTemplates: 0,
      } satisfies OrganizationItem;
    }
  } catch {
    // Fallback search completed without match
  }

  return null;
};

export const useGetOrganizationByOrgId = (orgId: string | undefined) => {
  return useQuery({
    queryKey: ['organization', 'orgId', orgId],
    queryFn: () => (orgId ? fetchOrganizationById(orgId) : null),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useActiveOrganization = () => {
  const orgId = useCurrentOrgId();
  return useGetOrganizationByOrgId(orgId ?? undefined);
};

export type SignatureOrgWithoutBlocks = {
  OrganizationId: string;
  OrganizationName: string;
  Role: string;
};

export type MyOrganization = OrganizationItem & { BlocksOrgId: string };

const normalizeOrgName = (name: string) => name.trim().toLowerCase();

export const parseSignatureOrgs = (sigData: unknown): SignatureOrgWithoutBlocks[] => {
  const payload = sigData as {
    data?: { organizations?: SignatureOrgWithoutBlocks[] };
    organizations?: SignatureOrgWithoutBlocks[];
  } | undefined;
  return payload?.data?.organizations ?? payload?.organizations ?? [];
};

/**
 * Hide Selise Signature orgs that are already synced to a Blocks OS org:
 * linked by SigOrgId, or sharing a name with a Blocks org the user already has.
 */
export const filterUnsyncedSignatureOrgs = (
  sigOrgs: SignatureOrgWithoutBlocks[],
  blocksOrgs: MyOrganization[],
  udsLinkedBySig: OrganizationItem[] = []
): SignatureOrgWithoutBlocks[] => {
  const syncedSigIds = new Set<string>();
  for (const org of blocksOrgs) {
    if (org.SigOrgId) syncedSigIds.add(org.SigOrgId);
  }
  for (const org of udsLinkedBySig) {
    if (org.SigOrgId) syncedSigIds.add(org.SigOrgId);
  }

  const blocksNames = new Set(
    blocksOrgs.map((org) => normalizeOrgName(org.Name)).filter(Boolean)
  );

  return sigOrgs.filter((sigOrg) => {
    if (syncedSigIds.has(sigOrg.OrganizationId)) return false;
    if (blocksNames.has(normalizeOrgName(sigOrg.OrganizationName))) return false;
    return true;
  });
};

export const useMyOrganizations = () => {
  const { data: iamData, isLoading: iamLoading } = useMyOgnization();
  const iamOrgs = useMemo(() => iamData?.organizations ?? [], [iamData?.organizations]);
  const orgIds = useMemo(() => iamOrgs.map((org) => org.itemId), [iamOrgs]);

  const { data: udsOrgs = [], isLoading: udsLoading } = useQuery({
    queryKey: ['my-uds-organizations', orgIds],
    enabled: orgIds.length > 0,
    queryFn: () => fetchOrganizationsByBlocksOrgIds(orgIds, false),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: sigData, isLoading: sigLoading } = useGetSignatureOrgs();
  const sigOrgs = useMemo(() => parseSignatureOrgs(sigData), [sigData]);
  const sigOrgIds = useMemo(() => sigOrgs.map((org) => org.OrganizationId), [sigOrgs]);

  const { data: udsLinkedBySig = [], isLoading: sigLinkLoading } = useQuery({
    queryKey: ['organizations-by-sig-ids', sigOrgIds],
    enabled: sigOrgIds.length > 0,
    queryFn: () => fetchOrganizationsBySigOrgIds(sigOrgIds),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const blocksOrgs = useMemo<MyOrganization[]>(() => {
    const byId = new Map(udsOrgs.map((org) => [org.BlocksOrgId, org]));
    return iamOrgs.map((iamOrg) => {
      const udsOrg = byId.get(iamOrg.itemId) ?? udsOrgs.find((org) => org.Name === iamOrg.name);
      if (udsOrg) return { ...udsOrg, BlocksOrgId: iamOrg.itemId, Name: udsOrg.Name || iamOrg.name };
      return {
        ItemId: iamOrg.itemId,
        BlocksOrgId: iamOrg.itemId,
        Name: iamOrg.name,
        Slug: '',
        MaxUsers: 0,
        MaxTemplates: 0,
      } satisfies OrganizationItem;
    });
  }, [iamOrgs, udsOrgs]);

  const sigOrgsWithoutBlocks = useMemo(
    () => filterUnsyncedSignatureOrgs(sigOrgs, blocksOrgs, udsLinkedBySig),
    [sigOrgs, blocksOrgs, udsLinkedBySig]
  );

  return {
    data: { blocksOrgs, sigOrgsWithoutBlocks },
    isLoading:
      iamLoading ||
      (orgIds.length > 0 && udsLoading) ||
      sigLoading ||
      (sigOrgIds.length > 0 && sigLinkLoading),
  };
};
export const GET_ORGANIZATIONS_QUERY = OrganizationsDocument;
